/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { TourData } from 'src/types/tour';
import { forwardRef } from 'react';
import { media } from 'src/styles/media';
import ColumnsText from 'src/components/columns-text';
import Container from 'src/components/core/layout/container';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = Partial<TourData['sections']['book']>;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: var(--color-dark);
  color: var(--color-white);
  position: relative;

  ${media.min.md`
     background-attachment: fixed;
     background-image: url('/images/saramago-transparent.png');
     background-repeat: no-repeat;
     background-size: contain;
     height: 100vh;
  `}

  ${media.max.md`
     margin-bottom: 5.75rem;
     padding: 3.125rem 0 7.188rem;
  `}
`;

/**
 * `ContainerStyle` styled component.
 */

const ContainerStyle = styled(Container)`
  ${media.max.md`
    position: relative;
  `}

  ${media.min.md`
    display: flex;
    height: 100%;
    padding: 14vw 4.5rem 6vw;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas: 'titleWrapper . image .';
    grid-template-columns: 3fr 0.4fr 4.6fr 0.4fr;
    width: 90vw;
  `}

  ${media.min.xl`
    width: 80vw;
  `}

  ${media.min.xxl`
    width: 63vw;
  `}
`;

/**
 * `Header` styled component.
 */

const Header = styled(Text).attrs({
  uppercase: true,
  variant: 'header'
})`
  color: var(--color-yellow);
  display: block;
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 25)}, ${25 / baseFontSize}rem);
  letter-spacing: clamp(${13 / baseFontSize}rem, ${clampCalc(13, 20)}, ${20 / baseFontSize}rem);
  line-height: clamp(${21 / baseFontSize}rem, ${clampCalc(21, 23)}, ${23 / baseFontSize}rem);
  margin-bottom: 1.25rem;

  ${media.min.md`
    margin: 0;
    padding-bottom: 30px;
    white-space: nowrap;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  font-size: clamp(${41 / baseFontSize}rem, ${clampCalc(41, 80)}, ${80 / baseFontSize}rem);
  letter-spacing: clamp(${2 / baseFontSize}rem, ${clampCalc(2, 4)}, ${4 / baseFontSize}rem);
  line-height: clamp(${32 / baseFontSize}rem, ${clampCalc(32, 73)}, ${73 / baseFontSize}rem);
  margin-bottom: 1rem;
  position: relative;
  text-transform: none;
  z-index: 2;
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  position: relative;

  ${media.max.md`
    padding-top: 18rem;
    margin-bottom: 3rem;
  `}

  ${media.min.md`
    grid-area: titleWrapper;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.max.md`
    margin-bottom: 3.25rem;
    padding-bottom: 100%;
  `}

  ${media.min.md`
    align-self: start;
    grid-area: image;
    height: 100%;
  `}
`;

/**
 * `BackgroundImageWrapper` styled component.
 */

const BackgroundImageWrapper = styled.div`
  ${media.max.md`
    position: absolute;
    width: 100%;
    padding-bottom: 128%;

    &::after {
      bottom: 0;
      content: '';
      height: 30%;
      left: 0;
      position: absolute;
      right: 0;
      background: linear-gradient(180deg, transparent 0%, var(--color-dark) 100%)
      0% 0%;
    }
  `}
`;

/**
 * `StyledColumnsText` styled component.
 */

const StyledColumnsText = styled(ColumnsText)`
  ${media.min.md`
    margin-bottom: 2rem;
  `}
`;

/**
 * `BookSection` component.
 */

const BookSection = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { header, image, mapName, text, title } = props;
  const isMobile = useBreakpoint('md', 'max');

  return (
    <Wrapper ref={ref}>
      {isMobile && (
        <FadeInUpAnimation options={{ transitionTime: 1 }}>
          <BackgroundImageWrapper>
            <Image alt={mapName} layout={'fill'} objectFit={'contain'} src={'/images/saramago-transparent.png'} />
          </BackgroundImageWrapper>
        </FadeInUpAnimation>
      )}

      <ContainerStyle fluid>
        <Grid>
          <TitleWrapper>
            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Header as={'h3'}>{header}</Header>
              </FadeInRightAnimation>
            )}

            {!isMobile && (
              <FadeInRightAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'quoteL'}>
                  {title}
                </Title>
              </FadeInRightAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Header as={'h3'}>{header}</Header>
              </FadeInUpAnimation>
            )}

            {isMobile && (
              <FadeInUpAnimation options={{ transitionTime: 1 }}>
                <Title as={'h2'} variant={'quoteL'}>
                  {title}
                </Title>
              </FadeInUpAnimation>
            )}
          </TitleWrapper>

          <ImageWrapper>
            {image && (
              <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
                <Image alt={mapName} layout={'fill'} objectFit={'contain'} objectPosition={'top'} src={image} />
              </FadeInAnimation>
            )}
          </ImageWrapper>
        </Grid>

        {text && <StyledColumnsText hasDarkTheme text={text} />}
      </ContainerStyle>
    </Wrapper>
  );
});

BookSection.displayName = 'BookSection';

/**
 * Export `BookSection` component.
 */

export default BookSection;
